import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Form, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { FiChevronDown, FiPlus } from "react-icons/fi";
import StatusPill from "@core/components/StatusPill";
import type { OriginScope } from "@features/report/template/types/reportTemplate";
import type { FinanceDeductible } from "@features/admin/finance/deductible/types/financeDeductible";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import SectionBuilder from "@features/presentationLayout/components/SectionBuilder";
import LayoutSections from "@features/presentationLayout/components/LayoutSections";
import {
  ADD_SECTION_LABEL,
  ADD_SECTION_MENU_TEST_ID,
  ADD_SECTION_TEST_ID,
  MAX_DESCRIPTION_LENGTH,
  MAX_NAME_LENGTH,
} from "@features/presentationLayout/constants/presentationLayoutConstants";
import {
  INCLUDE_FLAG_BY_ORIGIN,
  ORIGIN_SCOPE_LABELS,
  SECTION_TYPES,
  SECTION_TYPE_HELP,
  SECTION_TYPE_LABELS,
  type PresentationLayoutDetail,
  type PresentationLayoutMeta,
  type UpsertPresentationLayoutPayload,
} from "@features/presentationLayout/types/presentationLayout";
import {
  bindingCatalog,
  draftFromDetail,
  emptyDraft,
  emptyIncludes,
  moveItem,
  newSection,
  previewSections,
  templatesForDraft,
  toPayload,
  validateDraft,
  type LayoutDraft,
} from "@features/presentationLayout/utils/layoutForm";

const TAB_DETAILS = "details";
const TAB_SECTIONS = "sections";
const TAB_PREVIEW = "preview";

/** react-bootstrap Dropdown trips React 19 children typing; runtime matches Dropdown. */
const AddSectionDropdown = Dropdown as unknown as React.FC<
  React.PropsWithChildren<Record<string, unknown>>
>;

type Props = {
  show: boolean;
  /** null creates a new layout. */
  layout: PresentationLayoutDetail | null;
  meta: PresentationLayoutMeta;
  deductibles: FinanceDeductible[];
  availableOriginScopes: OriginScope[];
  /** Open-tag lock: structure (includes and sections) is read-only on existing layouts. */
  structureLocked: boolean;
  lockedReason: string | null;
  busy: boolean;
  onHide: () => void;
  onSave: (payload: UpsertPresentationLayoutPayload) => void;
};

export default function PresentationLayoutEditorModal({
  show,
  layout,
  meta,
  deductibles,
  availableOriginScopes,
  structureLocked,
  lockedReason,
  busy,
  onHide,
  onSave,
}: Props) {
  const isNew = layout === null;
  const defaultOrigin: OriginScope = availableOriginScopes[0] ?? "NATION";
  const [draft, setDraft] = useState<LayoutDraft>(() => emptyDraft(defaultOrigin));
  const [tab, setTab] = useState(TAB_DETAILS);
  const [sectionHelpOpen, setSectionHelpOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const showValidation = useErrorModalStore((s) => s.showValidation);

  useEffect(() => {
    if (!show) return;
    setDraft(layout ? draftFromDetail(layout) : emptyDraft(defaultOrigin));
    setTab(TAB_DETAILS);
    setSectionHelpOpen(false);
    setFieldErrors({});
  }, [show, layout, defaultOrigin]);

  const catalog = useMemo(() => bindingCatalog(meta), [meta]);
  const templates = useMemo(() => templatesForDraft(draft, meta), [draft, meta]);
  const preview = useMemo(() => previewSections(draft, meta, deductibles), [draft, meta, deductibles]);
  const allowedIncludes = meta.allowed_includes[draft.origin_scope] ?? [];
  const structureDisabled = busy || (!isNew && structureLocked);

  const patch = (changes: Partial<LayoutDraft>) => setDraft((current) => ({ ...current, ...changes }));

  const handleSave = () => {
    const result = validateDraft(draft, meta, deductibles);
    if (result.messages.length > 0) {
      setFieldErrors(result.fieldErrors);
      showValidation(result.messages);
      return;
    }
    setFieldErrors({});
    onSave(toPayload(draft, meta, deductibles));
  };

  return (
    <Modal
      show={show}
      onHide={() => !busy && onHide()}
      size="xl"
      centered
      scrollable
      backdrop="static"
      keyboard={!busy}
      data-testid="presentation-layout-editor-modal"
    >
      <Modal.Header closeButton={!busy}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Modal.Title>{isNew ? "New presentation layout" : `Edit ${layout.name}`}</Modal.Title>
          {layout ? <StatusPill active={layout.is_active} /> : null}
        </div>
      </Modal.Header>
      <Modal.Body>
        {!isNew && structureLocked ? (
          <Alert variant="warning" data-testid="layout-structure-locked">
            {lockedReason}
          </Alert>
        ) : null}

        <Tabs activeKey={tab} onSelect={(key) => setTab(key ?? TAB_DETAILS)} className="mb-3" mountOnEnter>
          <Tab eventKey={TAB_DETAILS} title="Details">
            <Form.Group className="mb-3" controlId="layoutName">
              <Form.Label className="fw-semibold">Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={draft.name}
                maxLength={MAX_NAME_LENGTH}
                disabled={busy}
                onChange={(e) => patch({ name: e.target.value })}
                data-testid="layout-name-input"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="layoutDescription">
              <Form.Label className="fw-semibold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={draft.description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                disabled={busy}
                onChange={(e) => patch({ description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="layoutOrigin">
              <Form.Label className="fw-semibold">Report level</Form.Label>
              {isNew ? (
                <Form.Select
                  value={draft.origin_scope}
                  disabled={busy}
                  onChange={(e) => {
                    const origin = e.target.value as OriginScope;
                    patch({ origin_scope: origin, ...emptyIncludes(origin) });
                  }}
                  data-testid="layout-origin-select"
                >
                  {availableOriginScopes.map((origin) => (
                    <option key={origin} value={origin}>{ORIGIN_SCOPE_LABELS[origin]} reports</option>
                  ))}
                </Form.Select>
              ) : (
                <div>{ORIGIN_SCOPE_LABELS[draft.origin_scope]} reports</div>
              )}
              <Form.Text>The layout appears on this level's department reports.</Form.Text>
            </Form.Group>
            {allowedIncludes.length > 1 ? (
              <Form.Group className="mb-3" data-testid="layout-includes">
                <Form.Label className="fw-semibold">Templates to include</Form.Label>
                {allowedIncludes.map((origin) => {
                  const flag = INCLUDE_FLAG_BY_ORIGIN[origin];
                  return (
                    <Form.Check
                      key={origin}
                      type="checkbox"
                      id={`layout-include-${origin}`}
                      label={`${ORIGIN_SCOPE_LABELS[origin]} templates`}
                      checked={draft[flag]}
                      disabled={structureDisabled}
                      onChange={(e) => patch({ [flag]: e.target.checked } as Partial<LayoutDraft>)}
                    />
                  );
                })}
              </Form.Group>
            ) : null}
          </Tab>

          <Tab eventKey={TAB_SECTIONS} title={`Sections (${draft.sections.length})`}>
            {draft.sections.map((section, index) => (
              <div key={section.key}>
                <SectionBuilder
                  section={section}
                  index={index}
                  count={draft.sections.length}
                  meta={meta}
                  catalog={catalog}
                  templates={templates}
                  deductibles={deductibles}
                  disabled={structureDisabled}
                  fieldErrors={fieldErrors}
                  onClearFieldError={(key) => setFieldErrors((current) => {
                    if (!(key in current)) return current;
                    const next = { ...current };
                    delete next[key];
                    return next;
                  })}
                  onChange={(next) => patch({ sections: draft.sections.map((s, i) => (i === index ? next : s)) })}
                  onMove={(to) => patch({ sections: moveItem(draft.sections, index, to) })}
                  onRemove={() => patch({ sections: draft.sections.filter((_, i) => i !== index) })}
                />
              </div>
            ))}
            {templates.length === 0 ? (
              <Alert variant="info" className="small">Choose templates to include on the Details tab before adding rows.</Alert>
            ) : null}
            <AddSectionDropdown>
              <Dropdown.Toggle
                variant="outline-primary"
                size="sm"
                disabled={structureDisabled || draft.sections.length >= meta.limits.sections}
                data-testid={ADD_SECTION_TEST_ID}
              >
                <FiPlus className="me-1" aria-hidden /> {ADD_SECTION_LABEL}
              </Dropdown.Toggle>
              <Dropdown.Menu
                data-testid={ADD_SECTION_MENU_TEST_ID}
                renderOnMount
                popperConfig={{
                  strategy: "fixed",
                  modifiers: [
                    { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                  ],
                }}
              >
                {meta.section_types.map((type) => (
                  <Dropdown.Item
                    key={type}
                    disabled={structureDisabled || draft.sections.length >= meta.limits.sections}
                    onClick={() => patch({ sections: [...draft.sections, newSection(type)] })}
                    data-testid={`add-section-${type}`}
                  >
                    {SECTION_TYPE_LABELS[type]}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </AddSectionDropdown>
            <div className="mt-3" data-testid="section-type-help">
              <button
                type="button"
                className="d-inline-flex align-items-center gap-1 border-0 bg-transparent p-0 text-muted small"
                aria-expanded={sectionHelpOpen}
                aria-controls="section-type-help-body"
                data-testid="section-type-help-toggle"
                onClick={() => setSectionHelpOpen((o) => !o)}
              >
                What do these section types do?
                <FiChevronDown
                  style={{ transform: sectionHelpOpen ? "rotate(180deg)" : undefined }}
                  aria-hidden
                />
              </button>
              {sectionHelpOpen ? (
                <div id="section-type-help-body" className="mt-2 text-muted small">
                  {meta.section_types.map((type, index) => {
                    const help = SECTION_TYPE_HELP[type];
                    return (
                      <div key={type} className={index > 0 ? "border-top pt-2 mt-2" : undefined} data-testid={`section-help-${type}`}>
                        <div className="fw-semibold text-body">{SECTION_TYPE_LABELS[type]}</div>
                        <div>{help.summary}</div>
                        {type === SECTION_TYPES.KPI_MATRIX ? (
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered mt-2 mb-1 w-auto">
                              <thead>
                                <tr>
                                  <th>Activity</th>
                                  <th className="text-end">Attendance</th>
                                  <th className="text-end">Visitors</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <th scope="row">Sunday Service</th>
                                  <td className="text-end">142</td>
                                  <td className="text-end">8</td>
                                </tr>
                                <tr>
                                  <th scope="row">Bible Study</th>
                                  <td className="text-end">36</td>
                                  <td className="text-end">2</td>
                                </tr>
                                <tr>
                                  <th scope="row">Youth Fellowship</th>
                                  <td className="text-end">28</td>
                                  <td className="text-end">5</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                        {type === SECTION_TYPES.BINDING_TABLE ? (
                          <>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mt-2 mb-1 w-auto">
                                <thead>
                                  <tr>
                                    <th />
                                    <th className="text-end">$</th>
                                    <th className="text-end">Total Attendance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <th scope="row">Tithes</th>
                                    <td className="text-end">12,000 (Tithes)</td>
                                    <td className="text-end">—</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Sunday School</th>
                                    <td className="text-end">450 (SS Offering)</td>
                                    <td className="text-end">36 (Attendance)</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="fst-italic">Parentheses show the source idea; on the report you see numbers.</div>
                          </>
                        ) : (
                          <div className="fst-italic">Example: {help.example}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </Tab>

          <Tab eventKey={TAB_PREVIEW} title="Preview">
            <p className="text-muted small">Structure only. Values appear on department reports.</p>
            <LayoutSections sections={preview} emptyMessage="Add sections to see a preview." />
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={busy} data-testid="save-layout-button">
          {busy ? <Spinner animation="border" size="sm" aria-hidden /> : isNew ? "Create layout" : "Save changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
